"use client";

import React, { createContext, useContext, useMemo } from "react";
import { Rules } from "./interfaces";
import ensureArray from "./utils/ensureArray";
import rolesHavePermissions from "./utils/rolesHavePermissions";

/**
 * Props for the AbacProvider component
 */
export interface AbacProviderProps<Role extends string, Permission extends string, User> {
  rules: Rules<Role, Permission, User>;
  children?: React.ReactNode;
  user?: User;
  roles?: Role[];
  permissions?: Permission[];
}

/**
 * Context value shape for ABAC
 */
export interface AbacContextProps<Permission extends string> {
  userHasPermissions: <Data>(permissions: Permission | Permission[], data?: Data) => boolean;
}

/**
 * Props for AllowedTo and NotAllowedTo components
 */
export interface AllowedToProps<Permission extends string> {
  perform?: Permission | Permission[];
  children?: React.ReactNode;
  fallback?: React.ReactNode;
  data?: any;
}

/**
 * Options for the secured HOC
 */
export interface SecuredOptions<Permission extends string, Props, Data> {
  permissions: Permission[] | Permission;
  noAccess?: React.ComponentType<Props>;
  mapPropsToData?(props: Props): Data;
}

/**
 * Factory function to create ABAC context, provider, and components
 * This allows for type-safe ABAC implementation with custom roles and permissions
 */
const create = <Role extends string, Permission extends string, User>() => {
  const AbacContextDefaults: AbacContextProps<Permission> = {
    userHasPermissions: () => {
      console.error("Can't call userHasPermissions, wrap your app with an <AbacProvider />.");
      return false;
    },
  };

  const AbacContext = createContext<AbacContextProps<Permission>>(AbacContextDefaults);

  /**
   * Provider component that wraps the application with ABAC context
   */
  const AbacProvider = ({ children, rules, roles = [], permissions = [], user }: AbacProviderProps<Role, Permission, User>) => {
    const userHasPermissions = useMemo(
      () => (requiredPermissions: Permission[] | Permission, data: any) => rolesHavePermissions(rules, roles, permissions, requiredPermissions, user, data),
      [rules, roles, permissions, user],
    );

    const contextValue = useMemo(() => ({ userHasPermissions }), [userHasPermissions]);

    return <AbacContext.Provider value={contextValue}>{children}</AbacContext.Provider>;
  };

  /**
   * Custom hook to access ABAC context
   */
  const useAbac = (): AbacContextProps<Permission> => {
    const context = useContext(AbacContext);

    if (context === AbacContextDefaults) {
      console.error("Can't use useAbac hook, wrap your app with an <AbacProvider />.");
    }

    return context;
  };

  /**
   * Component that renders children only if user has required permissions
   */
  const AllowedTo = ({ perform = [], children, fallback = null, data }: AllowedToProps<Permission>) => {
    const ctx = useAbac();

    if (ctx === AbacContextDefaults) {
      console.error("Can't render <AllowedTo />, wrap your app with an <AbacProvider />.");
      return null;
    }

    const hasPermission = ctx.userHasPermissions(ensureArray(perform), data);

    if (hasPermission) {
      return <>{children}</>;
    }

    return <>{fallback}</>;
  };

  /**
   * Component that renders children only if user does NOT have required permissions
   */
  const NotAllowedTo = ({ perform = [], children, fallback = null, data }: AllowedToProps<Permission>) => {
    const ctx = useAbac();

    if (ctx === AbacContextDefaults) {
      console.error("Can't render <NotAllowedTo />, wrap your app with an <AbacProvider />.");
      return null;
    }

    const hasPermission = ctx.userHasPermissions(ensureArray(perform), data);

    if (!hasPermission) {
      return <>{children}</>;
    }

    return <>{fallback}</>;
  };

  /**
   * Higher-order component that secures a component with permission checks
   */
  const secured =
    <Props, Data>({ permissions, noAccess: No, mapPropsToData }: SecuredOptions<Permission, Props, Data>) =>
    <T extends React.ComponentType<Props>>(Component: T): T => {
      const SecuredComponent: React.FC<Props> = (props: Props) => {
        const ctx = useAbac();

        if (ctx === AbacContextDefaults) {
          console.error("Can't render secured component, wrap your app with an <AbacProvider />.");
          return null;
        }

        const data = mapPropsToData?.(props);
        const hasPermission = ctx.userHasPermissions(ensureArray(permissions), data);

        if (hasPermission) {
          const ComponentToRender = Component as React.ComponentType<any>;
          return <ComponentToRender {...props} />;
        }

        if (No) {
          const NoAccessComponent = No as React.ComponentType<any>;
          return <NoAccessComponent {...props} />;
        }

        return null;
      };

      // Preserve component metadata for better debugging
      SecuredComponent.displayName = `Secured(${Component.displayName || Component.name || "Component"})`;

      // Copy static properties from the wrapped component
      return Object.assign(SecuredComponent, Component) as unknown as T;
    };

  return {
    AbacProvider,
    AllowedTo,
    AbacContext,
    NotAllowedTo,
    AbacContextDefaults,
    secured,
    useAbac,
  };
};

export default create;
